import { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, resolveImageUrl } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit3, Trash2, X, LogOut, Star, Package, Tag, Home as HomeIcon, ChevronUp, ChevronDown } from 'lucide-react';
import ImageUploader from '@/components/ImageUploader';

const ALL_GENDERS = [
  { slug: 'ladies', name: 'Ladies' },
  { slug: 'gents', name: 'Gents' },
  { slug: 'unisex', name: 'Unisex' },
  { slug: 'kids', name: 'Kids' },
];

const emptyForm = {
  name: '', category: '', subcategory: '', gender: 'ladies', price: '', description: '', fabric: '',
  images: [], sizes: '', colors: '', in_stock: true, featured: false, made_to_order: false,
};

const emptyCat = {
  slug: '', name: '', description: '', order: 100,
  cover_image: null,
  allowed_genders: ['ladies', 'gents', 'unisex', 'kids'],
  subcategories: [],
};

const emptyHome = { hero_image: null, hero_headline: '', hero_subline: '', showcase_images: [], manifesto_images: [], wholesale_images: [] };

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export default function AdminDashboard() {
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [catForm, setCatForm] = useState(emptyCat);
  const [newSubName, setNewSubName] = useState('');
  const [homeForm, setHomeForm] = useState(emptyHome);
  const [saving, setSaving] = useState(false);
  const [savingHome, setSavingHome] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('mb_token');
    if (!token) navigate('/admin/login');
    else load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c, h] = await Promise.all([
        api.get('/products'),
        api.get('/categories'),
        api.get('/home'),
      ]);
      setProducts(p.data);
      setCategories(c.data);
      setHomeForm({ ...emptyHome, ...(h.data || {}) });
    } catch (e) {
      if (e?.response?.status === 401) navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  const selectedCat = useMemo(() => categories.find((c) => c.slug === form.category), [categories, form.category]);
  const selectedSub = selectedCat?.subcategories?.find((s) => s.slug === form.subcategory);
  // Sub's allowed_genders (fallback to category's)
  const allowedForProduct = (selectedSub?.allowed_genders?.length ? selectedSub.allowed_genders : selectedCat?.allowed_genders) || ['ladies', 'gents', 'unisex', 'kids'];
  const subcategoriesForForm = selectedCat?.subcategories || [];

  // Product handlers
  const openCreateProduct = () => {
    const firstCat = categories[0];
    setForm({ ...emptyForm, category: firstCat?.slug || '', gender: firstCat?.allowed_genders?.[0] || 'ladies' });
    setModal({ mode: 'create', entity: 'product' });
  };
  const openEditProduct = (p) => {
    setForm({
      name: p.name, category: p.category, subcategory: p.subcategory || '',
      gender: p.gender || 'ladies', price: p.price,
      description: p.description, fabric: p.fabric || '',
      images: p.images || [],
      sizes: (p.sizes || []).join(', '),
      colors: (p.colors || []).join(', '),
      in_stock: p.in_stock, featured: p.featured, made_to_order: !!p.made_to_order,
    });
    setModal({ mode: 'edit', entity: 'product', product: p });
  };

  useEffect(() => {
    if (!selectedCat || modal?.entity !== 'product') return;
    if (!allowedForProduct.includes(form.gender)) {
      setForm((f) => ({ ...f, gender: allowedForProduct[0] || 'ladies' }));
    }
    if (form.subcategory && !selectedCat.subcategories?.some((s) => s.slug === form.subcategory)) {
      setForm((f) => ({ ...f, subcategory: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.category, form.subcategory, selectedCat?.slug]);

  const openCreateCat = () => { setCatForm(emptyCat); setNewSubName(''); setModal({ mode: 'create', entity: 'category' }); };
  const openEditCat = (c) => {
    setCatForm({
      slug: c.slug,
      name: c.name,
      description: c.description || '',
      order: c.order,
      cover_image: c.cover_image || null,
      allowed_genders: c.allowed_genders?.length ? c.allowed_genders : ['ladies', 'gents', 'unisex', 'kids'],
      subcategories: c.subcategories || [],
    });
    setNewSubName('');
    setModal({ mode: 'edit', entity: 'category', cat: c });
  };
  const close = () => setModal(null);

  const submitProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      category: form.category,
      subcategory: form.subcategory || null,
      gender: form.gender,
      price: Number(form.price) || 0,
      description: form.description.trim(),
      fabric: form.fabric.trim(),
      images: form.images,
      sizes: form.sizes.split(',').map((s) => s.trim()).filter(Boolean),
      colors: form.colors.split(',').map((s) => s.trim()).filter(Boolean),
      in_stock: !!form.in_stock,
      featured: !!form.featured,
      made_to_order: !!form.made_to_order,
    };
    try {
      if (modal.mode === 'create') { await api.post('/products', payload); toast.success('Product created'); }
      else { await api.put(`/products/${modal.product.id}`, payload); toast.success('Product updated'); }
      close(); load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Save failed');
    } finally { setSaving(false); }
  };

  // Category modal — gender + subcategory helpers (functional updates to avoid stale state)
  const toggleGender = (g) => {
    setCatForm((prev) => {
      const has = prev.allowed_genders.includes(g);
      if (has && prev.allowed_genders.length === 1) { toast.error('Keep at least one option'); return prev; }
      return { ...prev, allowed_genders: has ? prev.allowed_genders.filter((x) => x !== g) : [...prev.allowed_genders, g] };
    });
  };
  const addSubcategory = () => {
    const name = newSubName.trim();
    if (!name) { toast.error('Type a sub-category name first'); return; }
    const slug = slugify(name);
    setCatForm((prev) => {
      const subs = prev.subcategories || [];
      if (subs.some((s) => s.slug === slug)) { toast.error('This sub already exists'); return prev; }
      const order = subs.length + 1;
      return { ...prev, subcategories: [...subs, { slug, name, order, allowed_genders: null }] };
    });
    setNewSubName('');
  };
  const removeSubcategory = (slug) => setCatForm((prev) => ({ ...prev, subcategories: (prev.subcategories || []).filter((s) => s.slug !== slug) }));
  const moveSub = (idx, dir) => {
    setCatForm((prev) => {
      const arr = [...(prev.subcategories || [])];
      const swap = idx + dir;
      if (swap < 0 || swap >= arr.length) return prev;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return { ...prev, subcategories: arr.map((s, i) => ({ ...s, order: i + 1 })) };
    });
  };
  const renameSub = (idx, name) => {
    setCatForm((prev) => {
      const arr = [...(prev.subcategories || [])];
      arr[idx] = { ...arr[idx], name };
      return { ...prev, subcategories: arr };
    });
  };
  const toggleSubGender = (idx, g) => {
    setCatForm((prev) => {
      const arr = [...(prev.subcategories || [])];
      const cur = arr[idx].allowed_genders;
      if (cur === null || cur === undefined) {
        const parent = prev.allowed_genders.slice();
        arr[idx] = { ...arr[idx], allowed_genders: parent.includes(g) ? parent.filter((x) => x !== g) : [...parent, g] };
      } else {
        const has = cur.includes(g);
        if (has && cur.length === 1) { toast.error('Keep at least one option in the sub'); return prev; }
        arr[idx] = { ...arr[idx], allowed_genders: has ? cur.filter((x) => x !== g) : [...cur, g] };
      }
      return { ...prev, subcategories: arr };
    });
  };
  const resetSubGenders = (idx) => {
    setCatForm((prev) => {
      const arr = [...(prev.subcategories || [])];
      arr[idx] = { ...arr[idx], allowed_genders: null };
      return { ...prev, subcategories: arr };
    });
  };

  const submitCategory = async (e) => {
    e.preventDefault();
    setSaving(true);
    if (catForm.allowed_genders.length === 0) { toast.error('Pick at least one gender'); setSaving(false); return; }
    const payload = {
      slug: slugify(catForm.slug),
      name: catForm.name.trim(),
      description: catForm.description.trim(),
      order: Number(catForm.order) || 0,
      cover_image: catForm.cover_image || null,
      allowed_genders: catForm.allowed_genders,
      subcategories: catForm.subcategories.map((s) => ({
        slug: s.slug,
        name: s.name,
        order: s.order,
        allowed_genders: s.allowed_genders?.length ? s.allowed_genders : null,
      })),
    };
    try {
      if (modal.mode === 'create') { await api.post('/categories', payload); toast.success('Category added'); }
      else { await api.put(`/categories/${modal.cat.id}`, payload); toast.success('Category updated'); }
      close(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const submitHome = async () => {
    setSavingHome(true);
    try {
      await api.put('/home', homeForm);
      toast.success('Home page updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSavingHome(false); }
  };

  const removeProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };
  const removeCategory = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try { await api.delete(`/categories/${c.id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Cannot delete'); }
  };

  const logout = () => { localStorage.removeItem('mb_token'); localStorage.removeItem('mb_email'); navigate('/admin/login'); };

  const tabBtn = (id, label, icon, count) => (
    <button
      onClick={() => setTab(id)}
      data-testid={`tab-${id}`}
      className={`text-left p-3 sm:p-5 border rounded-sm ${tab === id ? 'bg-[color:var(--mb-primary)] text-white border-[color:var(--mb-primary)]' : 'bg-[color:var(--mb-surface)] border-[color:var(--mb-border)] hover:border-[color:var(--mb-primary)]'}`}
      style={{ transition: 'background-color 200ms ease, border-color 200ms ease' }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">{icon}<span className="text-[0.6rem] sm:text-[0.65rem] tracking-[0.25em] uppercase font-medium">{label}</span></div>
          {count !== null && count !== undefined && (
            <p className={`mt-1 text-[0.65rem] sm:text-xs ${tab === id ? 'text-white/85' : 'text-[color:var(--mb-text-muted)]'}`}>{count} {label === 'Home' ? '' : count === 1 ? 'item' : 'items'}</p>
          )}
        </div>
        {count !== null && count !== undefined && (
          <span className={`font-serif-display italic text-2xl sm:text-4xl shrink-0 ${tab === id ? 'text-white/60' : 'text-[color:var(--mb-primary)] opacity-60'}`}>{count}</span>
        )}
      </div>
    </button>
  );

  return (
    <div className="min-h-screen bg-[color:var(--mb-bg)] text-[color:var(--mb-text)]" data-testid="admin-dashboard">
      <header className="border-b border-[color:var(--mb-border)] px-4 sm:px-10 py-3 sm:py-4 flex items-center justify-between sticky top-0 bg-[color:var(--mb-bg)]/90 backdrop-blur-lg z-40">
        <Link to="/" className="flex items-center gap-2 sm:gap-3">
          <img src="/brand/mark.jpg" alt="" className="h-9 sm:h-10 w-auto" style={{ mixBlendMode: 'multiply' }} />
          <div className="flex flex-col leading-none">
            <span className="font-serif-display text-lg sm:text-xl text-[color:var(--mb-primary)] lowercase" style={{ fontWeight: 500 }}>mahabir</span>
            <span className="text-[0.55rem] sm:text-[0.6rem] tracking-[0.3em] text-[color:var(--mb-text-muted)]">ADMIN PANEL</span>
          </div>
        </Link>
        <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2 text-[0.65rem] tracking-[0.2em] uppercase border border-[color:var(--mb-border)] rounded-full hover:border-[color:var(--mb-primary)] hover:text-[color:var(--mb-primary)]" style={{ transition: 'border-color 200ms ease, color 200ms ease' }}>
          <LogOut size={12} /> Logout
        </button>
      </header>

      <main className="px-4 sm:px-10 py-6 sm:py-10 max-w-[1600px] mx-auto pb-24">
        <div className="mb-6 sm:mb-10 grid grid-cols-3 gap-2 sm:gap-3">
          {tabBtn('products', 'Products', <Package size={14} />, products.length)}
          {tabBtn('categories', 'Categories', <Tag size={14} />, categories.length)}
          {tabBtn('home', 'Home Page', <HomeIcon size={14} />, null)}
        </div>

        {tab === 'products' && (
          <>
            <div className="mb-4 sm:mb-6 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="eyebrow text-[0.6rem] sm:text-[0.7rem] mb-1">Inventory</p>
                <h1 className="font-serif-display text-2xl sm:text-4xl">All Products</h1>
              </div>
              <button onClick={openCreateProduct} data-testid="admin-new-product-btn" className="btn-red text-[0.65rem] sm:text-xs px-3 py-2 sm:px-4" disabled={categories.length === 0}>
                <Plus size={14} /> New Product
              </button>
            </div>

            {loading ? <p className="eyebrow" data-testid="admin-loading">Loading…</p>
            : products.length === 0 ? (
              <div className="border border-dashed border-[color:var(--mb-border)] bg-[color:var(--mb-surface)] p-8 sm:p-16 text-center" data-testid="products-empty-state">
                <Package size={36} className="inline text-[color:var(--mb-primary)] mb-4" />
                <h3 className="font-serif-display text-2xl sm:text-3xl mb-3">No products yet</h3>
                <p className="text-[color:var(--mb-text-muted)] max-w-md mx-auto text-sm mb-4">Tap <strong>New Product</strong> above.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5" data-testid="products-grid">
                {products.map((p) => {
                  const cat = categories.find((c) => c.slug === p.category);
                  const sub = cat?.subcategories?.find((s) => s.slug === p.subcategory);
                  return (
                    <div key={p.id} className="border border-[color:var(--mb-border)] bg-[color:var(--mb-surface)] p-3 sm:p-4 flex gap-3">
                      <div className="w-20 h-24 sm:w-24 sm:h-28 shrink-0 bg-[color:var(--mb-bg)] overflow-hidden border border-[color:var(--mb-border)] flex items-center justify-center">
                        {p.images?.[0] && <img src={resolveImageUrl(p.images[0])} alt="" className="max-w-full max-h-full object-contain" />}
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-[0.6rem] tracking-[0.25em] uppercase text-[color:var(--mb-text-muted)] mb-0.5 truncate">{cat?.name || p.category}{sub ? ` · ${sub.name}` : ''} · {p.gender}</p>
                            <h3 className="font-serif-display text-base sm:text-lg leading-tight truncate">{p.name}</h3>
                            <p className="mt-1 text-sm text-[color:var(--mb-primary)]">{p.made_to_order && !p.price ? 'On Enquiry' : `₹${Number(p.price).toLocaleString('en-IN')}`}</p>
                          </div>
                          {p.featured && <Star size={14} className="text-[color:var(--mb-primary)] shrink-0 mt-0.5" fill="currentColor" />}
                        </div>
                        <div className="flex items-center gap-2 mt-auto pt-2">
                          <span className={`text-[0.55rem] tracking-widest uppercase ${p.in_stock ? 'text-[color:var(--mb-primary)]' : 'text-red-500'}`}>{p.in_stock ? 'In stock' : 'Out'}</span>
                          <span className="text-[color:var(--mb-text-muted)]">·</span>
                          <button onClick={() => openEditProduct(p)} data-testid={`edit-${p.id}`} className="text-[0.6rem] tracking-widest uppercase hover:text-[color:var(--mb-primary)] inline-flex items-center gap-1" style={{ transition: 'color 200ms ease' }}><Edit3 size={11} /> Edit</button>
                          <button onClick={() => removeProduct(p)} data-testid={`delete-${p.id}`} className="text-[0.6rem] tracking-widest uppercase hover:text-red-500 inline-flex items-center gap-1 ml-auto" style={{ transition: 'color 200ms ease' }}><Trash2 size={11} /> Delete</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {tab === 'categories' && (
          <>
            <div className="mb-4 sm:mb-6 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="eyebrow text-[0.6rem] sm:text-[0.7rem] mb-1">Cultural Categories</p>
                <h1 className="font-serif-display text-2xl sm:text-4xl">All Categories</h1>
              </div>
              <button onClick={openCreateCat} data-testid="admin-new-category-btn" className="btn-red text-[0.65rem] sm:text-xs px-3 py-2 sm:px-4">
                <Plus size={14} /> New Category
              </button>
            </div>
            {loading ? <p className="eyebrow">Loading…</p>
            : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="categories-grid">
                {categories.map((c) => (
                  <div key={c.id} className="border border-[color:var(--mb-border)] bg-[color:var(--mb-surface)] flex flex-col" data-testid={`category-card-${c.slug}`}>
                    {c.cover_image ? (
                      <div className="aspect-[16/10] bg-[color:var(--mb-bg-alt)] border-b border-[color:var(--mb-border)] flex items-center justify-center overflow-hidden">
                        <img src={resolveImageUrl(c.cover_image)} alt={c.name} className="max-w-full max-h-full object-contain" />
                      </div>
                    ) : (
                      <div className="aspect-[16/10] bg-[color:var(--mb-bg-alt)] border-b border-[color:var(--mb-border)] flex items-center justify-center">
                        <span className="font-serif-display italic text-5xl text-[color:var(--mb-primary)]/25">{String(c.order).padStart(2, '0')}</span>
                      </div>
                    )}
                    <div className="p-4 flex flex-col flex-1">
                      <p className="text-[0.6rem] tracking-[0.3em] uppercase text-[color:var(--mb-primary)] font-medium mb-1">Ch. {String(c.order).padStart(2, '0')}</p>
                      <h3 className="font-serif-display text-lg sm:text-xl leading-tight">{c.name}</h3>
                      <p className="text-[0.65rem] text-[color:var(--mb-text-muted)] mt-0.5 mb-2">{c.slug}</p>
                      <div className="text-[0.65rem] text-[color:var(--mb-text-muted)] mb-1"><span className="tracking-widest uppercase text-[color:var(--mb-text)]/70">For:</span> {(c.allowed_genders || []).join(' · ') || 'All'}</div>
                      <div className="text-[0.65rem] text-[color:var(--mb-text-muted)] mb-3"><span className="tracking-widest uppercase text-[color:var(--mb-text)]/70">Subs:</span> {c.subcategories?.length ? c.subcategories.map((s) => s.name).join(' · ') : 'None'}</div>
                      <div className="flex items-center gap-2 mt-auto pt-2 border-t border-[color:var(--mb-border)]">
                        <button onClick={() => openEditCat(c)} data-testid={`edit-cat-${c.slug}`} className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-[0.6rem] tracking-widest uppercase border border-[color:var(--mb-border)] hover:border-[color:var(--mb-primary)] hover:text-[color:var(--mb-primary)]" style={{ transition: 'border-color 200ms ease, color 200ms ease' }}><Edit3 size={11} /> Edit</button>
                        <button onClick={() => removeCategory(c)} data-testid={`delete-cat-${c.slug}`} className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-[0.6rem] tracking-widest uppercase border border-[color:var(--mb-border)] hover:border-red-500 hover:text-red-500" style={{ transition: 'border-color 200ms ease, color 200ms ease' }}><Trash2 size={11} /> Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'home' && (
          <div data-testid="home-tab">
            <div className="mb-4 sm:mb-6">
              <p className="eyebrow text-[0.6rem] sm:text-[0.7rem] mb-1">Landing Page</p>
              <h1 className="font-serif-display text-2xl sm:text-4xl">Home Page Editor</h1>
              <p className="mt-2 text-sm text-[color:var(--mb-text-muted)]">Set the cover photo, edit the headline and manage the showcase reel of photos.</p>
            </div>

            <div className="space-y-8 max-w-3xl">
              {/* Hero cover */}
              <div className="border border-[color:var(--mb-border)] bg-[color:var(--mb-surface)] p-4 sm:p-6">
                <p className="eyebrow mb-3">Hero Cover Photo</p>
                <ImageUploader
                  value={homeForm.hero_image ? [homeForm.hero_image] : []}
                  onChange={(imgs) => setHomeForm({ ...homeForm, hero_image: imgs[0] || null })}
                  singleMode
                />
              </div>

              {/* Hero copy */}
              <div className="border border-[color:var(--mb-border)] bg-[color:var(--mb-surface)] p-4 sm:p-6 grid gap-4">
                <div>
                  <label className="eyebrow block mb-1.5">Hero Headline (3–4 words · last word turns red)</label>
                  <input
                    value={homeForm.hero_headline || ''}
                    onChange={(e) => setHomeForm({ ...homeForm, hero_headline: e.target.value })}
                    placeholder="Wear Your Heritage."
                    data-testid="home-headline"
                    className="w-full px-3 py-3 sm:py-2 bg-[color:var(--mb-bg)] border border-[color:var(--mb-border)] text-sm rounded-sm"
                  />
                  <p className="mt-1 text-xs text-[color:var(--mb-text-muted)]">Leave empty to keep the default.</p>
                </div>
                <div>
                  <label className="eyebrow block mb-1.5">Hero Subline (below headline)</label>
                  <textarea
                    rows={3}
                    value={homeForm.hero_subline || ''}
                    onChange={(e) => setHomeForm({ ...homeForm, hero_subline: e.target.value })}
                    placeholder="A quiet house of Nepali cultural clothing…"
                    data-testid="home-subline"
                    className="w-full px-3 py-3 sm:py-2 bg-[color:var(--mb-bg)] border border-[color:var(--mb-border)] text-sm rounded-sm resize-none"
                  />
                </div>
              </div>

              {/* Showcase reel */}
              <div className="border border-[color:var(--mb-border)] bg-[color:var(--mb-surface)] p-4 sm:p-6">
                <p className="eyebrow mb-1">Showcase Reel</p>
                <p className="text-xs text-[color:var(--mb-text-muted)] mb-3">Photos that scroll horizontally below the hero. Add as many as you want.</p>
                <ImageUploader
                  value={homeForm.showcase_images || []}
                  onChange={(imgs) => setHomeForm({ ...homeForm, showcase_images: imgs })}
                />
              </div>

              {/* Manifesto section images */}
              <div className="border border-[color:var(--mb-border)] bg-[color:var(--mb-surface)] p-4 sm:p-6">
                <p className="eyebrow mb-1">Manifesto Photos (3 sections)</p>
                <p className="text-xs text-[color:var(--mb-text-muted)] mb-3">The three big story sections on your home page — Legacy, Communities, Made to Order. First photo → Legacy, Second → Communities, Third → Made to Order. Leave any missing and it will show a chapter numeral instead.</p>
                <ImageUploader
                  value={homeForm.manifesto_images || []}
                  onChange={(imgs) => setHomeForm({ ...homeForm, manifesto_images: imgs })}
                  maxImages={3}
                />
              </div>

              {/* Wholesale section images */}
              <div className="border border-[color:var(--mb-border)] bg-[color:var(--mb-surface)] p-4 sm:p-6">
                <p className="eyebrow mb-1">Wholesale Photos (up to 4)</p>
                <p className="text-xs text-[color:var(--mb-text-muted)] mb-3">Shown in the Wholesale section on the home page. Add up to 4 photos of your bulk / wholesale stock.</p>
                <ImageUploader
                  value={homeForm.wholesale_images || []}
                  onChange={(imgs) => setHomeForm({ ...homeForm, wholesale_images: imgs })}
                  maxImages={4}
                />
              </div>

              <div className="flex justify-end">
                <button onClick={submitHome} disabled={savingHome} data-testid="home-save" className="btn-red text-xs px-6 py-3">
                  {savingHome ? 'Saving…' : 'Save Home Page'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Product modal */}
      {modal?.entity === 'product' && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-2 sm:p-4 overflow-y-auto" onClick={close}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[color:var(--mb-surface)] border border-[color:var(--mb-border)] w-full max-w-2xl my-4 sm:my-8" data-testid="product-modal">
            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-[color:var(--mb-border)] sticky top-0 bg-[color:var(--mb-surface)] z-10">
              <h2 className="font-serif-display text-xl sm:text-2xl">{modal.mode === 'create' ? 'New Product' : 'Edit Product'}</h2>
              <button onClick={close} data-testid="modal-close" className="p-2 hover:text-[color:var(--mb-primary)]" style={{ transition: 'color 200ms ease' }}><X size={18} /></button>
            </div>
            <form onSubmit={submitProduct} className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="eyebrow block mb-1.5">Photos</label>
                <ImageUploader value={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} />
              </div>
              <div className="sm:col-span-2">
                <label className="eyebrow block mb-1.5">Name</label>
                <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="form-name" className="w-full px-3 py-3 sm:py-2 bg-[color:var(--mb-bg)] border border-[color:var(--mb-border)] text-sm rounded-sm" />
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Category</label>
                <select required value={form.category} onChange={(e) => setForm({...form, category: e.target.value, subcategory: ''})} data-testid="form-category" className="w-full px-3 py-3 sm:py-2 bg-[color:var(--mb-bg)] border border-[color:var(--mb-border)] text-sm rounded-sm">
                  <option value="">Select…</option>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Sub-category {subcategoriesForForm.length === 0 && '(none)'}</label>
                <select value={form.subcategory} onChange={(e) => setForm({...form, subcategory: e.target.value})} data-testid="form-subcategory" disabled={subcategoriesForForm.length === 0} className="w-full px-3 py-3 sm:py-2 bg-[color:var(--mb-bg)] border border-[color:var(--mb-border)] text-sm rounded-sm disabled:opacity-50">
                  <option value="">— None —</option>
                  {subcategoriesForForm.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="eyebrow block mb-1.5">For (Gender)</label>
                <div className="flex flex-wrap gap-2">
                  {ALL_GENDERS.filter((g) => allowedForProduct.includes(g.slug)).map((g) => (
                    <button key={g.slug} type="button" onClick={() => setForm({...form, gender: g.slug})} data-testid={`form-gender-${g.slug}`} className={`chip ${form.gender === g.slug ? 'active' : ''}`}>{g.name}</button>
                  ))}
                </div>
                <p className="mt-1 text-xs text-[color:var(--mb-text-muted)]">Options come from this {selectedSub ? 'sub-category' : 'category'}. Manage under Categories → Edit.</p>
              </div>
              <div className="sm:col-span-2">
                <label className="eyebrow block mb-1.5">Price (₹) — set 0 for "On Enquiry"</label>
                <input type="number" inputMode="decimal" min="0" step="0.01" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} data-testid="form-price" className="w-full px-3 py-3 sm:py-2 bg-[color:var(--mb-bg)] border border-[color:var(--mb-border)] text-sm rounded-sm" />
              </div>
              <div className="sm:col-span-2">
                <label className="eyebrow block mb-1.5">Description</label>
                <textarea required rows={4} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="form-description" className="w-full px-3 py-3 sm:py-2 bg-[color:var(--mb-bg)] border border-[color:var(--mb-border)] text-sm resize-none rounded-sm" />
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Fabric</label>
                <input value={form.fabric} onChange={(e) => setForm({...form, fabric: e.target.value})} data-testid="form-fabric" className="w-full px-3 py-3 sm:py-2 bg-[color:var(--mb-bg)] border border-[color:var(--mb-border)] text-sm rounded-sm" />
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Sizes (comma-separated)</label>
                <input value={form.sizes} onChange={(e) => setForm({...form, sizes: e.target.value})} data-testid="form-sizes" placeholder="S, M, L" className="w-full px-3 py-3 sm:py-2 bg-[color:var(--mb-bg)] border border-[color:var(--mb-border)] text-sm rounded-sm" />
              </div>
              <div className="sm:col-span-2">
                <label className="eyebrow block mb-1.5">Colors (comma-separated)</label>
                <input value={form.colors} onChange={(e) => setForm({...form, colors: e.target.value})} data-testid="form-colors" placeholder="Red, Gold" className="w-full px-3 py-3 sm:py-2 bg-[color:var(--mb-bg)] border border-[color:var(--mb-border)] text-sm rounded-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm py-2"><input type="checkbox" checked={form.in_stock} onChange={(e) => setForm({...form, in_stock: e.target.checked})} data-testid="form-instock" className="accent-[color:var(--mb-primary)] w-4 h-4" />In stock</label>
              <label className="flex items-center gap-2 text-sm py-2"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})} data-testid="form-featured" className="accent-[color:var(--mb-primary)] w-4 h-4" />Featured on Home</label>
              <label className="sm:col-span-2 flex items-center gap-2 text-sm py-2"><input type="checkbox" checked={form.made_to_order} onChange={(e) => setForm({...form, made_to_order: e.target.checked})} data-testid="form-mto" className="accent-[color:var(--mb-primary)] w-4 h-4" />Made to Order piece</label>
              <div className="sm:col-span-2 flex flex-col-reverse sm:flex-row justify-end gap-3 mt-3 pt-4 border-t border-[color:var(--mb-border)] sticky bottom-0 bg-[color:var(--mb-surface)] py-3">
                <button type="button" onClick={close} className="btn-outline text-xs px-4 py-3 sm:py-2 justify-center">Cancel</button>
                <button type="submit" disabled={saving} data-testid="form-submit" className="btn-red text-xs px-4 py-3 sm:py-2 justify-center">{saving ? 'Saving…' : 'Save Product'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category modal */}
      {modal?.entity === 'category' && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-2 sm:p-4 overflow-y-auto" onClick={close}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[color:var(--mb-surface)] border border-[color:var(--mb-border)] w-full max-w-2xl my-4 sm:my-8" data-testid="category-modal">
            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-[color:var(--mb-border)] sticky top-0 bg-[color:var(--mb-surface)] z-10">
              <h2 className="font-serif-display text-xl sm:text-2xl">{modal.mode === 'create' ? 'New Category' : 'Edit Category'}</h2>
              <button onClick={close} data-testid="cat-modal-close" className="p-2 hover:text-[color:var(--mb-primary)]" style={{ transition: 'color 200ms ease' }}><X size={18} /></button>
            </div>
            <form onSubmit={submitCategory} className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">

              {/* Cover photo */}
              <div className="sm:col-span-2">
                <label className="eyebrow block mb-1.5">Cover Photo (shown on home page)</label>
                <ImageUploader
                  value={catForm.cover_image ? [catForm.cover_image] : []}
                  onChange={(imgs) => setCatForm({ ...catForm, cover_image: imgs[0] || null })}
                  singleMode
                />
              </div>

              <div className="sm:col-span-2">
                <label className="eyebrow block mb-1.5">Name (display)</label>
                <input required value={catForm.name} onChange={(e) => setCatForm({...catForm, name: e.target.value, slug: catForm.slug || slugify(e.target.value)})} data-testid="cat-form-name" className="w-full px-3 py-3 sm:py-2 bg-[color:var(--mb-bg)] border border-[color:var(--mb-border)] text-sm rounded-sm" />
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Slug (URL)</label>
                <input required value={catForm.slug} onChange={(e) => setCatForm({...catForm, slug: e.target.value})} data-testid="cat-form-slug" className="w-full px-3 py-3 sm:py-2 bg-[color:var(--mb-bg)] border border-[color:var(--mb-border)] text-sm rounded-sm" />
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Order</label>
                <input type="number" inputMode="numeric" value={catForm.order} onChange={(e) => setCatForm({...catForm, order: e.target.value})} data-testid="cat-form-order" className="w-full px-3 py-3 sm:py-2 bg-[color:var(--mb-bg)] border border-[color:var(--mb-border)] text-sm rounded-sm" />
              </div>
              <div className="sm:col-span-2">
                <label className="eyebrow block mb-1.5">Description</label>
                <textarea rows={3} value={catForm.description} onChange={(e) => setCatForm({...catForm, description: e.target.value})} data-testid="cat-form-description" className="w-full px-3 py-3 sm:py-2 bg-[color:var(--mb-bg)] border border-[color:var(--mb-border)] text-sm resize-none rounded-sm" />
              </div>

              {/* Allowed genders */}
              <div className="sm:col-span-2">
                <label className="eyebrow block mb-2">Allowed Options (For) — this category</label>
                <div className="flex flex-wrap gap-2" data-testid="cat-allowed-genders">
                  {ALL_GENDERS.map((g) => (
                    <button type="button" key={g.slug} onClick={() => toggleGender(g.slug)} data-testid={`cat-gender-${g.slug}`} className={`chip ${catForm.allowed_genders.includes(g.slug) ? 'active' : ''}`}>{g.name}</button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-[color:var(--mb-text-muted)]">Tap to add/remove. Products in this category will be restricted to these options — unless a sub-category overrides them below.</p>
              </div>

              {/* Subcategories manager */}
              <div className="sm:col-span-2 border-t border-[color:var(--mb-border)] pt-4">
                <label className="eyebrow block mb-2">Sub-categories ({catForm.subcategories.length})</label>
                <p className="text-xs text-[color:var(--mb-text-muted)] mb-3">Break a category into styles (e.g. Tamang Dress → Ghagra Style, Rafer Style). Each sub can also have its own For options — leave "Inherit" to use the category's options.</p>

                {catForm.subcategories.length > 0 && (
                  <div className="flex flex-col gap-3 mb-3" data-testid="cat-subcategories-list">
                    {catForm.subcategories.map((s, idx) => {
                      const isInherit = s.allowed_genders === null || s.allowed_genders === undefined;
                      const genders = isInherit ? catForm.allowed_genders : s.allowed_genders;
                      return (
                        <div key={s.slug} className="bg-[color:var(--mb-bg)] border border-[color:var(--mb-border)] p-3" data-testid={`cat-sub-${s.slug}`}>
                          <div className="flex items-center gap-2">
                            <span className="font-serif-display italic text-[color:var(--mb-primary)] text-lg w-6 text-center">{idx + 1}</span>
                            <input value={s.name} onChange={(e) => renameSub(idx, e.target.value)} data-testid={`cat-sub-name-${s.slug}`} className="flex-1 px-2 py-1.5 bg-transparent border-b border-transparent focus:border-[color:var(--mb-primary)] outline-none text-sm" style={{ transition: 'border-color 200ms ease' }} />
                            <button type="button" onClick={() => moveSub(idx, -1)} disabled={idx === 0} data-testid={`cat-sub-up-${s.slug}`} className="p-1 disabled:opacity-30 hover:text-[color:var(--mb-primary)]"><ChevronUp size={14} /></button>
                            <button type="button" onClick={() => moveSub(idx, 1)} disabled={idx === catForm.subcategories.length - 1} data-testid={`cat-sub-down-${s.slug}`} className="p-1 disabled:opacity-30 hover:text-[color:var(--mb-primary)]"><ChevronDown size={14} /></button>
                            <button type="button" onClick={() => removeSubcategory(s.slug)} data-testid={`cat-sub-remove-${s.slug}`} className="p-1 hover:text-red-500"><Trash2 size={14} /></button>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-8">
                            <span className="text-[0.55rem] tracking-[0.25em] uppercase text-[color:var(--mb-text-muted)] mr-1">For:</span>
                            {ALL_GENDERS.map((g) => (
                              <button
                                type="button"
                                key={g.slug}
                                onClick={() => toggleSubGender(idx, g.slug)}
                                data-testid={`cat-sub-gender-${s.slug}-${g.slug}`}
                                className={`px-2 py-1 text-[0.6rem] tracking-widest uppercase rounded-full border ${genders?.includes(g.slug) ? 'bg-[color:var(--mb-primary)] text-white border-[color:var(--mb-primary)]' : 'border-[color:var(--mb-border)] text-[color:var(--mb-text-muted)]'}`}
                                style={{ transition: 'background-color 200ms ease, border-color 200ms ease, color 200ms ease' }}
                              >
                                {g.name}
                              </button>
                            ))}
                            {!isInherit && (
                              <button type="button" onClick={() => resetSubGenders(idx)} data-testid={`cat-sub-inherit-${s.slug}`} className="text-[0.55rem] tracking-widest uppercase text-[color:var(--mb-primary)] hover:underline ml-1">
                                inherit
                              </button>
                            )}
                            {isInherit && <span className="text-[0.55rem] tracking-widest uppercase text-[color:var(--mb-text-muted)]">(inherits)</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex gap-2">
                  <input value={newSubName} onChange={(e) => setNewSubName(e.target.value)} placeholder="New sub-category name…" data-testid="cat-sub-input" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSubcategory(); } }} className="flex-1 px-3 py-2 bg-[color:var(--mb-bg)] border border-[color:var(--mb-border)] text-sm rounded-sm" />
                  <button type="button" onClick={addSubcategory} data-testid="cat-sub-add" className="btn-red text-[0.65rem] px-3 py-2 shrink-0"><Plus size={14} /> Add</button>
                </div>
              </div>

              <div className="sm:col-span-2 flex flex-col-reverse sm:flex-row justify-end gap-3 mt-3 pt-4 border-t border-[color:var(--mb-border)]">
                <button type="button" onClick={close} className="btn-outline text-xs px-4 py-3 sm:py-2 justify-center">Cancel</button>
                <button type="submit" disabled={saving} data-testid="cat-form-submit" className="btn-red text-xs px-4 py-3 sm:py-2 justify-center">{saving ? 'Saving…' : 'Save Category'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
